<template>
  <div class="w-80 h-100 mx-auto mt-6 getting-started-box">
    <div class="row flex-column justify-space-between mx-0">
      <small v-if="showPaymentStep" class="mt-1">
        You have to first setup your payment method to initiate Onboarding.
      </small>
      <div v-else>
        <h1>Getting Started</h1>
        <small class="mt-1">
          You must be an Administrator of your Office 365 account in order to
          set up Xift.
        </small>
      </div>
    </div>
    <x-dialog
      :dialogState="showDialog"
      :dialogTitle="dialogTitle"
      :dialogText="dialogText"
      @toggleModal="toggleModal"
      :confirmDialog="true"
      :data-test-id="`${dataTestId}__skip-connectors-confirmation-dialog`"
      :actions="{
        show: true,
        secondaryButtonFunction: toggleModal,
        primaryButtonFunction: () => jumpStep(true),
        primaryButtonShow: true,
        secondaryButtonText: 'Cancel',
        primaryButtonText: 'Continue'
      }"
    ></x-dialog>
    <div class="w-50 h-100 mt-12 chargify-padding" v-if="showPaymentStep">
      <div class="mb-12">
        <h2>Setup your payment method</h2>
        <p class="font12 mt-2">Please add your preferred payment method.</p>
      </div>
      <loader v-if="isSubmitButtonDisabled" />
      <chargify
        @createPaymentProfile="addPaymentToken"
        @disableSubmitButton="disableSubmitButton"
      >
        <x-button
          color="#58e2c2"
          buttonText="Submit"
          slot="chargify-button"
          buttonType="submit"
          :disabled="isSubmitButtonDisabled"
        />
      </chargify>
    </div>
    <v-stepper
      v-else
      vertical
      v-model="currentStep"
      class="no-box-shadow mx-0 mt-12"
    >
      <v-row>
        <v-col md="4" sm="5" lg="3" class="border-right-1">
          <v-stepper-step
            :complete="currentStep > index + 1"
            v-for="(step, index) in steps"
            :step="`${index + 1}`"
            :key="index"
            >{{ step.shortTitle || step.title }}</v-stepper-step
          >
        </v-col>
        <v-col>
          <v-stepper-content step="1" :class="{ hide: currentStep !== 1 }">
            <div>
              <div :inner-html.prop="content | sanitize"></div>
              <sso
                type="vault"
                :showToggleButton="true"
                @callback="ssoConnectorCallBack"
                :showOnly="['o365_group']"
                :showButtons="false"
              ></sso>
            </div>
          </v-stepper-content>
          <v-stepper-content step="2" :class="{ hide: currentStep !== 2 }">
            <div>
              <div :inner-html.prop="content | sanitize" />
              <sso
                type="sso"
                :showToggleButton="true"
                @callback="ssoConnectorCallBack"
                :showButtons="false"
              />
              <div class="mt-8">
                <x-button
                  v-if="canSkipSSO"
                  :color="'#58e2c2'"
                  buttonText="Continue"
                  class="mt-12"
                  @click="moveToNext"
                  :data-test-id="`${dataTestId}__configure-sso__continue-btn`"
                />
              </div>
            </div>
          </v-stepper-content>

          <v-stepper-content step="3" :class="{ hide: currentStep !== 3 }">
            <div>
              <div :inner-html.prop="content | sanitize" />

              <sso
                :showToggleButton="true"
                :showButtons="false"
                :showOnly="showOnly"
                type="vault"
                @callback="ssoConnectorCallBack"
                @toggleLoader="toggleLoader"
              />
              <div class="mt-8">
                <x-button
                  v-if="canSkipStep"
                  :color="'#58e2c2'"
                  buttonText="Continue"
                  class="mt-12"
                  @click="jumpStep"
                  :data-test-id="
                    `${dataTestId}__configure-connectors__continue-btn`
                  "
                />
              </div>
            </div>
          </v-stepper-content>
          <v-stepper-content step="4" :class="{ hide: currentStep !== 4 }">
            <div>
              <div class="mb-12">
                <div :inner-html.prop="content | sanitize" />
                <x-button
                  :color="'#58e2c2'"
                  buttonText="Launch Xift"
                  class="mt-4"
                  @click="launchXift('trigger')"
                  :data-test-id="
                    `${dataTestId}__set-up-complete__launch-xift-btn`
                  "
                />
              </div>
            </div>
          </v-stepper-content>
          <v-stepper-content step="5">
            <div>
              <div class="mb-12">
                <div class="mb-12">
                  <h2>Thanks !</h2>
                  <p class="font12 mt-2">
                    Thank you for completing the Xift account setup. You can
                    change settings at any time in the Configuration menu.
                  </p>
                  <x-button
                    :color="'#58e2c2'"
                    buttonText="Launch Xift"
                    class="mt-4"
                    @click="launchXift"
                    :data-test-id="`${dataTestId}__thanks__launch-xift-btn`"
                  />
                </div>
              </div>
            </div>
          </v-stepper-content>
        </v-col>
      </v-row>
    </v-stepper>
    <loader v-if="loading" />
  </div>
</template>
<script>
import { getKeyByValue } from '@/helpers/common';
import UserService from '../../services/UserService';
import PublicAPIService from '../../services/PublicAPIService';
import Chargify from '@/components/Common/Chargify';
import sso from '../Organisations/Overview/SSO';
import { steps, stepKeys, dialogText } from './stepsData';
import { setTimeout } from 'timers';
import config from '../../../config';
import OnboardingService from '../../services/OnboardingService';
import BillingService from '../../services/BillingService';
import { setCookie, getCookie } from '@/helpers/cookie';
import { mapMutations, mapState } from 'vuex';

export default {
  components: {
    sso,
    Chargify
  },
  data() {
    return {
      steps: steps || [],
      showPaymentStep: false,
      dialogTitle:
        'Are you sure you want to continue without adding any connectors?',
      dialogText: dialogText,
      showDialog: false,
      stepsKeys: stepKeys || {},
      pollCount: 0,
      pollLength: 50,
      pollSpeed: 30000,
      pollHandler: null,
      loadingStatus:
        'Please wait, While we are processing the things at our end.',
      paymentToken: '',
      isSubmitButtonDisabled: false,
      loading: false,
      dataTestId: 'onboarding-steps'
    };
  },
  computed: {
    ...mapState([
      'userInformation',
      'singleSignOnStatus',
      'activeConnectors',
      'currentSubscription'
    ]),
    showOnly() {
      let connectors = ['o365_sharepoint', 'o365_onedrive', 'o365_email'];
      if (
        this.$can('view', 'dropbox') &&
        (this.currentSubscription?.product?.handle !== 'trial' ||
          this.isCurrentOrgLegacy)
      ) {
        connectors.push('dropbox');
      }

      if (this.$can('view', 'teams')) {
        connectors.push('o365_teams');
      }
      return connectors;
    },
    currentStep: {
      get() {
        const step = this.stepsKeys[this.onboardingStatus];
        if (step) {
          return step;
        } else {
          return 1;
        }
      },
      set(value) {
        const stepName = getKeyByValue(this.stepsKeys, value);
        this.setOnboardingStatus(stepName);
        // return value;
      }
    },
    /**
     * @return no. of vault connectors have consent
     */
    connectors() {
      const vaultConnectors =
        this.activeConnectors.filter(
          ({
            data: {
              subscriptionDetails: { SOURCE_ID, HAS_CONSENT }
            }
          }) => this.showOnly.includes(SOURCE_ID) && HAS_CONSENT
        ) || [];
      return vaultConnectors.length;
    },
    canSkipStep() {
      // eslint-disable-next-line
      return this.currentStep === 3;
    },
    canSkipSSO() {
      return (
        // eslint-disable-next-line
        this.currentStep === 2 && this.singleSignOnStatus?.HAS_CONSENT
      );
    },
    content() {
      if (this.steps.length < this.currentStep) {
        return;
      }
      const stepContent = this.steps[this.currentStep - 1];

      return `<div>
                <h2 >${stepContent.title}</h2>
                <p class="font12 mt-2">${stepContent.description}</p>
              </div>`;
    }
  },
  mounted() {
    setCookie('ssoAuthWindow', false);
    this.initialiseOnboarding();
    this.getUserSubscription();
  },

  created() {
    this.getUserPaymentStatus();
  },

  methods: {
    ...mapMutations(['setOnboardingStatus']),
    /**
     * Get's a callback whenever there is change is connector or sso status
     * Interpret the response and check if it has @var HAS_CONSENT true
     * If yes then checks currentStep is 3, 1 or other
     * Changes the onboarding Status then
     * @param {*} response
     */
    async ssoConnectorCallBack(response) {
      if (response.HAS_CONSENT) {
        if (this.canSkipStep) {
          return;
        }
        this.moveToNext();
        // eslint-disable-next-line
        // if (this.currentStep === 3 || this.currentStep === 2) {
        //   await BillingService.syncMails(this.currentlySelectedOrgId);
        // }
      }
    },
    async getUserPaymentStatus() {
      const response = await PublicAPIService.getUserRegisterStepStatus(
        this.userInformation?.primaryEmail || ''
      );
      const parsedResponse = this.parseResponse(response);
      if (parsedResponse?.[0]?.signUpStep === 'PAYMENT_GATEWAY') {
        this.showPaymentStep = true;
      } else {
        this.showPaymentStep = false;
      }
    },
    disableSubmitButton(type) {
      if (type === 'card') {
        this.isSubmitButtonDisabled = true;
      }
    },
    async setUserSignUpStatus() {
      const response = await PublicAPIService.getUserRegisterStepStatus(
        this.userInformation.primaryEmail || '',
        'THANKS'
      );
      const parsedResponse = this.parseResponse(response);
      return parsedResponse;
    },
    async initialiseOnboarding() {
      await OnboardingService.onboardingOrg(
        this.currentlySelectedOrgId,
        'initialise'
      );
    },
    async moveToNext() {
      // eslint-disable-next-line
      this.currentStep += 1;
      await this.changeOnboardingStatus(
        getKeyByValue(this.stepsKeys, this.currentStep)
      );
    },
    openAuthWindow(route, windowId = 'sso') {
      return window.open(
        route,
        windowId,
        'location=yes,height=550,width=550,scrollbars=yes,status=yes'
      );
    },
    async activeWindowTimer() {
      clearInterval(this.windowInterVal);
      var timeoutPeriod1 = 1000;
      this.windowInterVal = setInterval(async () => {
        const windowStatus = getCookie('ssoAuthWindow');
        // eslint-disable-next-line
        if (windowStatus == 'true') {
          clearInterval(this.windowInterVal);
          try {
            setCookie('ssoAuthWindow', false);
            // window.localStorage.setItem('ssoAuthWindow', false);

            await this.moveToNext();
          } catch (err) {
            console.log(err);
          }
        }
      }, timeoutPeriod1);
      var timeoutPeriod2 = 300000;
      setTimeout(() => {
        clearInterval(this.windowInterVal);
      }, timeoutPeriod2);
    },
    async makeAsDefaultPayment(profileId) {
      try {
        await BillingService.defaultPaymentProfile(profileId);
      } catch (error) {
        this.snackbar('error', this.$message.ERROR);
      }
    },
    /**
     * @param {String} token
     */
    async addPaymentToken(token) {
      this.isSubmitButtonDisabled = true;
      this.paymentToken = token;
      if (!this.paymentToken) {
        this.isSubmitButtonDisabled = false;
        return this.snackbar('error', this.$message.FILL_FIELDS);
      }
      try {
        const response = await BillingService.createPaymentProfile(
          this.paymentToken
        );
        const paymentProfile = this.parseResponse(response);
        if (paymentProfile) {
          const response = await PublicAPIService.getUserRegisterStepStatus(
            this.userInformation?.primaryEmail || '',
            'THANKS'
          );
          const parsedResponse = this.parseResponse(response);
          // if network call fails to update signupstatus = THANKS, we will fire again that call after 5 secs
          if (!parsedResponse) {
            setTimeout(() => {
              this.setUserSignUpStatus();
              // eslint-disable-next-line
            }, 5000);
          }
          this.snackbar(
            'success',
            'Your payment method has been added successfully'
          );
          this.makeAsDefaultPayment(paymentProfile?.[0]?.id);
          this.showPaymentStep = false;
          // this.moveToNext();
        }
      } catch {
        this.snackbar('error', this.$message.ERROR);
      } finally {
        this.isSubmitButtonDisabled = false;
      }
    },

    async subscribeMailSync() {
      const url = `https://login.microsoftonline.com/common/oauth2/authorize?response_type=code&redirect_uri=${config.callbackrlForSSO}&client_id=${config.mailsSyncClientId}&prompt=admin_consent&state=mailsync`;
      this.activeWindowTimer();
      this.openAuthWindow(url);

      // try {

      //   await MailSyncService.requestMailSync({});
      //   await this.moveToNext();
      // } catch (err) {
      //   this.snackbar('error', err);
      // }
    },
    /**
     * Get's current onboarding status of the step
     */

    /**
     *
     * Set onboarding status of current org
     */
    changeOnboardingStatus(onboardingStatus) {
      return UserService.setOnboardingStatus(undefined, onboardingStatus);
    },

    /**
     * Jump Step if the current status is 3
     * check's if @param skip is true
     * if yes, then toggle's the modal, change step and set onboarding status
     * else if, connector's is on then change step and set onboarding status
     * else shows modal
     * @param {boolean} skip
     */
    async jumpStep(skip) {
      if (skip) {
        this.toggleModal();
        this.currentStep = this.currentStep + 1;
        await this.changeOnboardingStatus(
          getKeyByValue(this.stepsKeys, this.currentStep)
        );
      } else if (this.connectors) {
        this.currentStep = this.currentStep + 1;
        await this.changeOnboardingStatus(
          getKeyByValue(this.stepsKeys, this.currentStep)
        );
      } else {
        this.toggleModal();
      }
    },

    /**
     * Toggle the modal
     */
    toggleModal() {
      this.showDialog = !this.showDialog;
    },
    toggleLoader(state) {
      this.loading = state;
    },
    /** Launch  base route */
    async launchXift(type) {
      try {
        this.loading = true;
        if (type === 'trigger') {
          await this.$services.onboarding.onboardingOrg(
            this.currentlySelectedOrgId,
            'trigger'
          );
          this.snackbar('info', this.$message.ONBOARDING_STARTED);
        }
        this.initalRoute('GO_TO_INITAL');
      } catch (error) {
        this.snackbar('error', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style>
.v-stepper__step__step {
  height: 36px !important ;
  width: 36px !important;
}
.v-application--is-ltr
  .theme--light.v-stepper--vertical
  .v-stepper__content:not(:last-child) {
  border-left: 0px !important;
}
.v-application--is-ltr
  .theme--light.v-stepper--vertical
  .v-stepper__content:not(:last-child) {
  margin: 0px !important;
}
.chargify-padding {
  padding-bottom: 15vh;
}
.getting-started-box {
  min-height: calc(100vh - 64px);
}
</style>
